// PatternContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Pattern1,
  Pattern2,
  Pattern3,
} from '../page/aiAnalyze/myAlgorithm/pattern/data';

interface PatternInfoProps {
  analysis_type: string;
  appraisal_point: string;
  bid_cnt: string;
  ctp: string;
  elapsed_years: string;
  emd: string | null;
  end_date: string;
  hidden_value: string;
  hug: string;
  investment_rate: string | null;
  item_category: string;
  lien: string;
  margin: string;
  market_prc: string;
  opposing_power: string;
  pnu: string;
  profit: string;
  resale: string;
  sale_days: string;
  sig: string | null;
  start_date: string;
  statutory_superficies: string;
  tomb_base_right: string;
  violation_building: string;
  wage_bond: string;
  zoning: string | null;
  create_time: string;
  option: string;
}

// Combine the states into one interface
interface PatternContextType {
  isStep1Done: boolean;
  setIsStep1Done: React.Dispatch<React.SetStateAction<boolean>>;
  isStep2Done: boolean;
  setIsStep2Done: React.Dispatch<React.SetStateAction<boolean>>;
  isStep3Done: boolean;
  setIsStep3Done: React.Dispatch<React.SetStateAction<boolean>>;
  isAnalysisDone: boolean;
  setIsAnalysisDone: React.Dispatch<React.SetStateAction<boolean>>;
  pIdx: number;
  setPIdx: React.Dispatch<React.SetStateAction<number>>;
  patternInfo: PatternInfoProps;
  setPatternInfo: React.Dispatch<React.SetStateAction<PatternInfoProps>>;
  isCheckAvaiable: boolean;
  setIsCheckAvaiable: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTab: string;
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>;
  sido: string;
  setSido: React.Dispatch<React.SetStateAction<string>>;
  sigungu: string;
  setSigungu: React.Dispatch<React.SetStateAction<string>>;
  emd: string;
  setEmd: React.Dispatch<React.SetStateAction<string>>;
  isLastTipOn: boolean;
  setIsLastTipOn: React.Dispatch<React.SetStateAction<boolean>>;
  pattern1: Pattern1;
  setPattern1: React.Dispatch<React.SetStateAction<Pattern1>>;
  pattern2: Pattern2;
  setPattern2: React.Dispatch<React.SetStateAction<Pattern2>>;
  pattern3: Pattern3;
  setPattern3: React.Dispatch<React.SetStateAction<Pattern3>>;
}

// Create the context with a default value
const PatternContext = createContext<PatternContextType | undefined>(undefined);

// Provider component to wrap around the app and provide context
export const PatternProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isStep1Done, setIsStep1Done] = useState<boolean>(false);
  const [isStep2Done, setIsStep2Done] = useState<boolean>(false);
  const [isStep3Done, setIsStep3Done] = useState<boolean>(false);
  const [isAnalysisDone, setIsAnalysisDone] = useState<boolean>(false);
  const [pIdx, setPIdx] = useState<number>(0);
  const [patternInfo, setPatternInfo] = useState<PatternInfoProps>({
    analysis_type: '',
    appraisal_point: '',
    bid_cnt: '',
    ctp: '',
    elapsed_years: '',
    emd: '',
    end_date: '',
    hidden_value: '',
    hug: '',
    investment_rate: '',
    item_category: '',
    lien: '',
    margin: '',
    market_prc: '',
    opposing_power: '',
    pnu: '',
    profit: '',
    resale: '',
    sale_days: '',
    sig: '',
    start_date: '',
    statutory_superficies: '',
    tomb_base_right: '',
    violation_building: '',
    wage_bond: '',
    zoning: '',
    create_time: '',
    option: '',
  });
  const [isCheckAvaiable, setIsCheckAvaiable] = useState<boolean>(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('분석 기준');
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [emd, setEmd] = useState('');
  const [isLastTipOn, setIsLastTipOn] = useState(false);
  const [pattern1, setPattern1] = useState<Pattern1>({
    type: 'A',
    region: { sido: '', sigungu: '', emd: '' },
    itemStatus: '',
    useArea: [],
    multiUseArea: [],
    analysisPeriod: { start: '2022-01-01', end: '2024-12-31' },
    pnu: '',
  });

  const [pattern2, setPattern2] = useState<Pattern2>({
    saleProfit: 'A',
    hiddenValue: 'A',
    takeProfit: 'N',
    marketPrice: '',
    appraisalPoint: '',
    bidCount: '',
    oldAge: '',
    saleDays: '',
    investmentAmount: '평균 실투자금',
    enteredAmount: '',
  });

  const [pattern3, setPattern3] = useState<Pattern3>({
    specialConditionOption: 'C',
    hugCondition: 'N',
    tenantsOpposition: 'N',
    wageBond: 'N',
    resale: 'N',
    violatingBuilding: 'N',
    statutoryLandRights: 'N',
    graveSiteRights: 'N',
    lien: 'N',
  });
  return (
    <PatternContext.Provider
      value={{
        isCheckAvaiable,
        setIsCheckAvaiable,
        isStep1Done,
        setIsStep1Done,
        isStep2Done,
        setIsStep2Done,
        isStep3Done,
        setIsStep3Done,
        isAnalysisDone,
        setIsAnalysisDone,
        pIdx,
        setPIdx,
        patternInfo,
        setPatternInfo,
        isEditMode,
        setIsEditMode,
        selectedTab,
        setSelectedTab,
        sido,
        setSido,
        sigungu,
        setSigungu,
        emd,
        setEmd,
        isLastTipOn,
        setIsLastTipOn,
        pattern1,
        setPattern1,
        pattern2,
        setPattern2,
        pattern3,
        setPattern3,
      }}
    >
      {children}
    </PatternContext.Provider>
  );
};

// Custom hook to use the PatternContext
export const usePatternContext = (): PatternContextType => {
  const context = useContext(PatternContext);
  if (!context) {
    throw new Error('usePatternContext must be used within a PatternProvider');
  }
  return context;
};
